import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, ApiError, type IssuanceJob } from "../lib/api.js";
import { Button } from "../components/ui/button.js";
import { Card, CardContent } from "../components/ui/card.js";
import { Input, Textarea } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";

const SAMPLE = "Name,Email,Issue Date,Grade\nAda Lovelace,ada@example.com,2026-07-01,A\n";

/** Bulk issuance (REQ-1.5): paste/upload CSV → issue → watch job progress. */
export function BulkIssuePage() {
  const { slug = "" } = useParams();
  const [params] = useSearchParams();
  const groupId = params.get("groupId") ?? "";

  const [achievement, setAchievement] = useState("");
  const [criteria, setCriteria] = useState("");
  const [csv, setCsv] = useState(SAMPLE);
  const [job, setJob] = useState<IssuanceJob | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) setCsv(await file.text());
  }

  async function pollJob(jobId: string) {
    for (let i = 0; i < 60; i++) {
      const { job: j } = await api.getJob(slug, jobId);
      setJob(j);
      if (j.status === "done" || j.status === "failed") return;
      await new Promise((r) => setTimeout(r, 1000));
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSummary(null);
    setJob(null);
    try {
      const res = await api.bulkIssue(slug, { groupId, achievement: { name: achievement, criteria }, csv });
      if (res.mode === "inline") {
        setSummary(`Issued ${res.issued ?? 0}, skipped ${res.skipped ?? 0} (already issued).`);
      } else {
        setSummary(`Queued ${res.queued ?? 0} credentials.`);
        await pollJob(res.jobId);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Bulk issue failed");
    } finally {
      setBusy(false);
    }
  }

  if (!groupId)
    return <p className="text-sm text-destructive">Missing group. Open this from a group&apos;s Issue link.</p>;

  const pct = job && job.total ? Math.round((job.processed / job.total) * 100) : 0;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <h1 className="text-2xl font-semibold">Bulk issue credentials</h1>
      <Card>
        <CardContent className="p-6">
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ach">Achievement name</Label>
                <Input id="ach" value={achievement} onChange={(e) => setAchievement(e.target.value)} required placeholder="Data Science 101" />
              </div>
              <div>
                <Label htmlFor="crit">Criteria (optional)</Label>
                <Input id="crit" value={criteria} onChange={(e) => setCriteria(e.target.value)} placeholder="Completed all modules" />
              </div>
            </div>
            <div>
              <Label htmlFor="csv">Recipients CSV (Name, Email, Issue Date, Grade)</Label>
              <Textarea id="csv" className="font-mono text-xs" rows={8} value={csv} onChange={(e) => setCsv(e.target.value)} />
              <input type="file" accept=".csv,text/csv" onChange={onFile} className="mt-2 text-sm" />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={busy}>
              {busy ? "Issuing…" : "Issue credentials"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {summary && (
        <Card>
          <CardContent className="p-4 text-sm">{summary}</CardContent>
        </Card>
      )}
      {job && (
        <Card>
          <CardContent className="p-4 text-sm">
            <div className="mb-2 flex justify-between">
              <span className="font-medium">Job {job.status}</span>
              <span className="text-muted-foreground">
                {job.processed}/{job.total} · {job.failed} failed
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
              <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${pct}%` }} />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
